import { useCallback, useEffect, useRef } from 'react'
import { useNotificationActions } from 'src/features/notification/store'
import { inventoryItemToNotification } from 'src/features/notification/factories/notifications.factories'
import { useGetCurrentGameId } from 'src/store/game'
import { useInventoryStore } from '../store'
import { isBonus } from '../types/domain'
import { isItemApplicableToGame } from '../utils/isItemApplicableToGame'
import { useClaimInventoryItem } from './useClaimInventoryItem'

export const useUnclaimedItemNotifications = () => {
  const items = useInventoryStore((state) => state.items)
  const currentGameId = useGetCurrentGameId()
  const { addNotification, removeNotification } = useNotificationActions()
  const { claimInventoryItem } = useClaimInventoryItem()

  // Track which items we've already shown notifications for to prevent duplicates
  const shownNotificationsRef = useRef<Set<string>>(new Set())

  // Track notification IDs for removal when items are removed/cancelled
  const notificationIdsRef = useRef<Map<string, string>>(new Map())

  // Use ref to avoid dependency issues and excessive re-renders
  const claimInventoryItemRef = useRef(claimInventoryItem)
  claimInventoryItemRef.current = claimInventoryItem

  const claimItemWithTracking = useCallback(async (itemId: string) => {
    await claimInventoryItemRef.current(itemId)
  }, []) // Empty dependency array prevents excessive re-renders

  useEffect(() => {
    // Find all unclaimed items applicable to the current game.
    // Free bets are handled by useAutoClaimFreeBets (one aggregated banner +
    // auto-claim), so exclude them here to avoid the per-item Claim banners.
    // Boosters keep their existing per-item flow.
    const unclaimedItems = items.filter(
      (item) =>
        item.status === 'claimable' &&
        (!currentGameId || isItemApplicableToGame(item, currentGameId)) &&
        !isBonus(item)
    )

    // Show notification for each unclaimed item we haven't shown yet
    unclaimedItems.forEach((item) => {
      if (!shownNotificationsRef.current.has(item.id)) {
        shownNotificationsRef.current.add(item.id)

        const notificationItem = inventoryItemToNotification(
          item,
          (key) => key,
          claimItemWithTracking
        )

        const notificationId = addNotification(notificationItem, true)

        // Store notification ID for potential removal later
        notificationIdsRef.current.set(item.id, notificationId)
      }
    })

    // Clean up tracking and notifications for items that are no longer in inventory or no longer claimable
    const currentItemIds = new Set(items.map((item) => item.id))
    const unclaimedItemIds = new Set(unclaimedItems.map((item) => item.id))
    const shownIds = Array.from(shownNotificationsRef.current)

    shownIds.forEach((id) => {
      const itemStillExists = currentItemIds.has(id)
      const itemStillClaimable = unclaimedItemIds.has(id)

      // Remove notification if item was removed from inventory OR is no longer claimable
      if (!itemStillExists || !itemStillClaimable) {
        // Remove the notification if we have its ID
        const notificationId = notificationIdsRef.current.get(id)
        if (notificationId) {
          removeNotification(notificationId)
          notificationIdsRef.current.delete(id)
        }

        // Remove from tracking
        shownNotificationsRef.current.delete(id)
      }
    })
  }, [items, currentGameId, addNotification, removeNotification])

  // Comprehensive cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear all refs to prevent memory leaks
      shownNotificationsRef.current.clear()
      notificationIdsRef.current.clear()
      claimInventoryItemRef.current = null as any
    }
  }, [])
}
